/**
 * HydraClient — browser WebSocket to Hydra's consumer `/ws` (deployed API is typically port 8891, e.g. `ws://hydra.newbx.com:8891/ws`).
 * Protocol: Hydra repo `CONSUMER_API.md`.
 *
 * **Auth:** Hydra's `ConsumerWebSocketServer` validates `Authorization: Bearer <consumerBearerToken>`
 * when that env is set. The browser `WebSocket` API cannot set custom headers, so this client connects
 * with the URL only. Use an open Hydra (no consumer token), same-origin proxy, or a non-browser client
 * if your deployment requires Bearer on the socket.
 *
 * **Connection sharing:** When the browser supports `SharedWorker`, all g1xl tabs in the
 * same browser session share a single upstream WS via `src/workers/hydraWorker.ts`.
 * This satisfies Hydra's 1-WS-per-service rule (HYDRA_API.md:16-55). Tabs in browsers
 * without SharedWorker support fall back to one direct WS each (legacy behaviour).
 *
 * **Protocol (matches Hydra consumer docs):**
 *   Client → Server: { op: 'subscribe', channel: 'all' | 'markets' | 'orderbooks' }
 *                     { op: 'snapshot', channel: 'markets' | 'orderbooks' }
 *                     { op: 'ping', ts: number }
 *   Server → Client: market | orderbook | pong | backpressure | error
 */

import { connectMarketOps, replaceMarketFilterOp } from '../workers/sportFilter';
import {
  FeedFilterAggregator,
  FilterFence,
  buildMarketFilter,
  filterKey,
  type FeedNeeds,
  type FilterResetMsg,
  type HydraFilterAckMsg,
  type MarketFilter,
  type MarketFilterEffective,
  type FencePair,
} from '../workers/feedFilter';
import { resolveFeedFilterMode, type FeedFilterMode } from './feedFilterConfig';
import type { KalshiPriceRange } from '../types';

export type { HydraFilterAckMsg, MarketFilterEffective, FencePair } from '../workers/feedFilter';

// ── Hydra message types ──────────────────────────────────────────

/** Kalshi series classes on the row (`class`, hydracore manifest-v2 §3.1). */
export type KalshiSeriesClass =
  | 'moneyline'
  | 'spread'
  | 'total'
  | 'btts'
  | 'futures'
  | CfbGameEventClass;

/** The CFB game-event subset g1 renders on GameDetail's Events tab
 *  (hydracore cfb-game-props). See `class` below. */
export type CfbGameEventClass =
  | 'half-fulltime'
  | 'first-td-team'
  | 'first-to-score'
  | 'overtime'
  | 'two-point'
  | 'every-quarter-btts'
  | 'highest-quarter'
  | 'team-stat'
  | 'team-total'
  | 'total-touchdowns'
  | 'total-fieldgoals'
  | 'player-prop';

export interface HydraMarket {
  ticker: string;
  eventTicker: string;
  /**
   * Kalshi matching-engine shard identity, forwarded by shard-aware producers.
   * Optional while older Hydra versions remain in service. Missing is unknown,
   * never shard zero; routing decisions live outside this read model.
   */
  exchangeIndex?: number;
  // Kalshi parent event series ticker, e.g. KXNBAWEST. Forwarded by newer
  // Hydra builds so consumers can classify sibling families without
  // hardcoding every raw market prefix.
  seriesTicker?: string;
  title: string;
  eventTitle?: string;
  // Per-market subtitle forwarded from Kalshi's `yes_sub_title`. Carries
  // the canonical line on spread + total frames — e.g. "Over 203.5 points
  // scored" or "Montreal wins by over 2.5 goals". Single source of truth
  // for line derivation; no per-sport bucket math needed when present.
  // Nullable on the wire: hydraedge sends an explicit `null` on Polymarket
  // frames (Poly carries no Kalshi-shaped subtitle). Typed to match.
  subtitle?: string | null;
  status: string;
  sport?: string;
  /** Scope of play. 'full' (default / full game) or a period: '1H' (1st half,
   *  Kalshi + Poly), '1Q'–'4Q' (single quarters, Kalshi-only), '2H' (soccer
   *  Game Lines), or 'F5' (MLB First 5 Innings, Kalshi-only). Set by hydra at
   *  discovery from the ticker so consumers bucket period markets WITHOUT
   *  re-parsing. Absent ⇒ treat as 'full'. See HYDRA_API.md "Period markets". */
  period?: 'full' | '1H' | '2H' | 'F5' | '1Q' | '2Q' | '3Q' | '4Q';
  category?: string;
  yesBid?: number;  // cents
  yesAsk?: number;  // cents
  noBid?: number;
  noAsk?: number;
  lastPrice?: number;
  volume?: number;
  openInterest?: number;
  // Scheduled start time of the parent event (game/match/fight), ISO UTC.
  // Sourced by Hydra from Polymarket Gamma `gameStartTime` and Kalshi
  // milestone `start_date` when available. With canonicalGameKey collapsing
  // venue siblings onto one row, one source can power the cross-venue display.
  // Undefined when upstream metadata has no start signal.
  eventStartTime?: string;
  source: 'kalshi' | 'polymarket';
  lastUpdateMs: number;
  /**
   * Kalshi-only authoritative banded order-price grid. Optional while mixed
   * Hydra versions are in service.
   */
  priceRanges?: KalshiPriceRange[];
  // Hydra-normalized identifiers. Present on dan-dev+; undefined on older
  // Hydra builds — consumers must tolerate that. See hydra/HYDRA_API.md.
  canonicalGameKey?: string;   // "sport|YYYY-MM-DD|TEAM_A|TEAM_B", alpha-sorted
  // Doubleheader-safe key (additive, dual-key — emitted alongside the
  // unchanged canonicalGameKey). Shape: "sport|YYYY-MM-DD|gameNo|TEAM_LO|TEAM_HI"
  // — lowercase sport, alpha-sorted teams, integer gameNo (1 for a normal
  // single game, >1 only for a doubleheader's later games). Uniform 5-seg for
  // every game, so consumers can group/address by it unconditionally. Two games
  // of the same matchup on one date share canonicalGameKey but differ here, so
  // grouping on this field keeps their odds + score un-blended. Absent on a
  // market discovered before Unabated's bootstrap populates (self-corrects on the
  // next discovery refresh) — fall back to canonicalGameKey then, UNLESS the
  // frame is ambiguous (see `dhAmbiguous` below, and the belt-and-braces
  // sibling check in marketStore for pre-`dhAmbiguous` producers), in which
  // case there is no safe fallback and the frame must fail closed.
  // See HYDRA_API.md.
  canonicalGameKeyV2?: string;
  // Fail-closed doubleheader signal (DNL-001). Set by hydraedge ONLY when it
  // proved the base key is a doubleheader day but could NOT say which game
  // this frame belongs to; `canonicalGameKeyV2` is then absent by producer
  // invariant (hydraedge ConsumerHub emits the flag only when true, so every
  // other frame is byte-identical to before). Consumers must NOT promote the
  // legacy key to gameNo=1 for such a frame — that would attach it to GAME 1
  // and put a wrong-game start time (and wrong-game trade context) on a
  // tradeable card. marketStore suppresses these from rows / ticker selection
  // / start-time slots while keeping them observable diagnostically; see
  // `resolveGameKeyV2` there and HYDRA_API.md "dhAmbiguous".
  dhAmbiguous?: boolean;
  canonicalAwayCode?: string;  // Kalshi only (Poly slugs order by YES-outcome)
  canonicalHomeCode?: string;  // Kalshi only
  // Source-agnostic alpha-sorted canonical pair. Set on both Kalshi and
  // Polymarket frames whenever the market parses into a game (per-day
  // moneyline / spread / total). Lets the row collapse off raw Poly slug
  // aliases (NYL→NY, POR→PDX, LAS→LV, GSV→GS for WNBA) even when Kalshi
  // hasn't listed the game yet. Not game-day-positional — use
  // canonicalAwayCode/canonicalHomeCode for that.
  canonicalTeams?: [string, string];
  /** Numeric line for a spread/total market — the STRUCTURED replacement for
   *  parsing Kalshi's `subtitle` prose. Added by hydraedge 2026-07-31
   *  (main @ e8fefad) on both `/ws` and REST; additive, `subtitle` still
   *  carries the prose. Present on both venues, expressing the same fixture's
   *  line as the same number.
   *
   *  ABSENT IS NOT ZERO. A moneyline legitimately has no line, and defaulting
   *  to 0 renders a bogus 0.5 line — treat absent as "not a line market" and
   *  fall through to the prose/bucket parse (OG hydra frames do not carry it).
   *  Necessary because Kalshi's spread wording is not stable across series:
   *  the World Cup says "Goal Diff Reg Time: England wins by more than 5.5
   *  goals" while MLS/UEL/UCL say "New York City wins by more than 2.5 goals". */
  floorStrike?: number | null;
  /** Upper bound for a RANGE market (both ends). Currently always absent —
   *  hydra ships no range markets yet. Reserved so consumers can distinguish a
   *  range from the one-sided `floorStrike` when they appear. */
  capStrike?: number | null;
  // Polymarket UFC frames only (2026-07-30). Order-independent COMPLETE-name
  // identity of the fight: both outcomes as playerKey-style slugs, alpha-sorted
  // and pipe-joined ("anderson-silva|joao-santos"). Exists because even
  // full-surname canonical keys are lossy — two same-day fights can share a
  // surname pair — and it is the evidence hydra's ambiguity guard uses.
  // INFORMATIONAL for consumers (disambiguation / display); the pairing
  // contract remains canonicalGameKey, which hydra WITHDRAWS entirely when a
  // key is ambiguous. See HYDRA_API.md "UFC fight markets".
  participantKey?: string;
  // Soccer 3-way / future N-outcome markets: canonical outcome that this
  // binary market's YES side pays on. Team code or "DRAW". Kalshi's raw draw
  // suffix is TIE and Polymarket's is draw; Hydra normalizes both here.
  // Nullable on the wire: spreads and totals carry an explicit `null` BY
  // DESIGN (OG parity - the edge leaves them unstamped). Never filter a
  // spread/total on this being present.
  yesOutcome?: string | null;
  // Market-type discriminator forwarded by hydra on soccer / derivative frames.
  // 'advance' = the knockout "To Advance" 2-way (who advances incl. ET + pens —
  // a DISTINCT market + settlement from the regulation 1X2, even though it shares
  // the 1X2's canonicalGameKey). marketStore cross-checks this against the
  // locally-parsed marketType so advance markets bucket APART from the 3-way and
  // never pollute it. Absent on older builds / non-soccer frames — consumers must
  // tolerate that and fall back to the parsed type. See HYDRA_API.md "Soccer To
  // Advance".
  // NOTE `'game'` is what hydraedge actually sends on soccer club-league
  // frames — for moneylines AND spreads/totals alike, so it does NOT
  // discriminate bet type. Use the series (KX…SPREAD / KX…TOTAL) or
  // `floorStrike`'s presence for that; this field is only load-bearing for the
  // 'advance' cross-check.
  // `'future'` = a DATELESS, gameless outright (tournament winner, season win
  // total, award). hydraedge stamps it on the Polymarket season-futures branch
  // (upstream/polymarket/discovery.ts) and it is the ONLY reliable signal that
  // a frame is futures-shaped — those tickers carry no date, no teams, and no
  // canonical keys for a consumer to infer one from. `'prop'` rides the same
  // branch for dated per-game props. Both are additive: g1's game selectors
  // ignore them, and getFutureBoards keys off `'future'` exclusively.
  //
  // WARNING for the futures path: `eventTicker` on a future frame is the
  // market's own conditionId, NOT a container — the Gamma event slug that
  // names the tournament is dropped at the edge. Group by the slug
  // (lib/futures/parseFutureSlug), never by eventTicker.
  marketType?: 'moneyline' | 'spread' | 'total' | 'series' | 'btts' | 'advance' | 'game' | 'future' | 'prop';
  /** The Kalshi SERIES CLASS on the row (hydracore cfb-game-props, main @
   *  eb77800, 2026-08-30 — HYDRA_API.md "Kalshi identity records"): core
   *  stamps it on every Kalshi identity record and edges forward it. This is
   *  what a consumer renders BY — class + `period` + `subtitle` (+
   *  `canonicalYesTeam` for sided rows) — never by re-parsing the ticker
   *  (CFB's 5-letter codes and `GTGT`/`TIEGT` combo suffixes defeat a
   *  client parser). `marketType` stays the coarse `game | future`
   *  discriminator it always was. Values: moneyline, spread, total, btts,
   *  team-total, half-fulltime, overtime, two-point, first-to-score,
   *  first-td-team, highest-quarter, every-quarter-btts, total-touchdowns,
   *  total-fieldgoals, team-stat (+ `stat`), player-prop, futures. Unknown
   *  → ignore the row for pane purposes (rule 8). Typed as the known union
   *  PLUS `string` so a value core ships before this file learns it still
   *  typechecks as "unknown". Absent on frames from an engine that has not
   *  forwarded it yet, and on Polymarket rows. */
  class?: KalshiSeriesClass | (string & {});
  /** `team-stat` rows only: which per-team stat the O/U line counts —
   *  `yds | td | fg | int | sack | to | rec | rec-td | rec-yds | rush-att |
   *  rush-td | rush-yds`. Absent on every other class. */
  stat?: string;
  // Set on every market frame for series-related tickers (Kalshi
  // KX*SERIES*/KX*SERIESSCORE*/KX*SERIESROADWIN* and Polymarket
  // *-playoffs-who-will-win-series-* slugs). Shape:
  //   "{sport}|series|{YY}|TEAM_A-TEAM_B"
  // Teams alpha-sorted, normalized canonical 3-char codes. Used by
  // marketStore to collapse cross-venue series tickers onto one row,
  // mirroring the canonicalGameKey path for per-day games.
  canonicalSeriesKey?: string;
  // Set on series tickers (Kalshi + Polymarket): the canonical 3-char
  // team code whose win resolves the binary contract YES. On Polymarket
  // slugs the YES team is encoded by slug position (not alpha order),
  // so this is the only reliable per-team mapping for series rows.
  // Falls back to "row's awayCode is YES" when undefined.
  canonicalYesTeam?: string;
  // Set on every series-related ticker frame (KX*SERIES, KX*SERIESSCORE,
  // KX*SERIESROADWIN, Polymarket who-will-win-series slugs).
  //
  //   seriesWins         — per-team wins keyed by canonical 3-char team code,
  //                        e.g. {BOS: 1, PHI: 2}. Undefined on pre-deploy /
  //                        pre-tracker-warmup frames or Polymarket-only series
  //                        where there are no Kalshi GAME tickers to count.
  //   seriesWinsToClinch — wins required to take the series. 4 for best-of-7,
  //                        3 for best-of-5, 2 for best-of-3. Derived from
  //                        Kalshi's KX*SERIESSCORE outcome shape; falls back
  //                        to 4 (NBA/NHL playoffs are always best-of-7).
  //
  // Used by clinch-injection: skip unless some team is on match point
  // (wins ≥ seriesWinsToClinch − 1). Series-not-started and no-team-on-hill
  // both fall through to "leave Kalshi cells blank when per-day ticker is
  // missing" rather than mis-injecting the series-winner ticker.
  seriesWins?: Record<string, number>;
  seriesWinsToClinch?: number;
  // Polymarket-only token plumbing. Populated by hydra ≥ 2026-05-16 from
  // its tokenRegistry (Gamma `conditionId` + `clobTokenIds[0]=yes,
  // clobTokenIds[1]=no`). Required by g1's trade ticket (GameDetailPanel)
  // to construct CLOB orders without a second lookup at trade time. Older hydra builds
  // leave all three undefined; consumers must tolerate that.
  conditionId?: string;
  yesTokenId?: string;
  noTokenId?: string;
  /**
   * Polymarket-only. True when the market belongs to a NEG_RISK
   * multi-outcome event (mutually-exclusive bundled yes/no markets —
   * NBA Draft first pick, election primary slots, etc.). Forwarded to
   * placeOrder via the v2 SDK or signing fails with `invalid signature`.
   * Older hydra builds leave this undefined; consumers must tolerate
   * that (treat absent as false).
   */
  negRisk?: boolean;
  /**
   * Polymarket-only. CLOB minimum price increment as a stringified
   * decimal — "0.01" for most sports moneylines, "0.001"/"0.0001" for
   * fine-tick binaries. Sourced from Gamma `orderPriceMinTickSize` via
   * hydra's CachedMarket. Forwarded to placeOrder so the v2 CLOB
   * doesn't reject with `invalid tick size (X), minimum for the market
   * is Y` on markets with sub-cent increments. Older hydra builds
   * (pre-2026-05-23) leave this undefined; relay-side `placeOrder`
   * defaults to '0.01' in that case, which is correct for every sports
   * moneyline we trade but wrong for fine-tick binaries — pass the
   * value through whenever it's present.
   */
  tickSize?: string;
}

export interface HydraPriceLevel {
  price: number;  // cents 1-99
  size: number;
}

export interface HydraOrderbook {
  ticker: string;
  yes: HydraPriceLevel[];
  no: HydraPriceLevel[];
  timestamp: number;
}

export interface HydraMarketMsg {
  type: 'market';
  market: HydraMarket;
  serverTs: number;
  seq: number;
  /** A2′ fencing pair — present on EVERY markets frame of a connection that
   *  sent the versioned `filter` (edge + core), absent on legacy frames.
   *  Admission is exact-pair equality against the last ack'd pair
   *  (`FilterFence`); any other stamp — an older generation, a previous
   *  socket — is dropped. */
  socketIncarnation?: string;
  filterGeneration?: number;
}

export interface HydraOrderbookMsg {
  type: 'orderbook';
  ticker: string;
  orderbook: HydraOrderbook;
  staleness?: { lastUpdateMs: number; ageMs: number; state: string };
  serverTs: number;
  seq: number;
}

/** Unabated consensus update for one (canonicalGameKey, periodTypeId) pair.
 *  Shape matches `UnabatedGameState.consensus` on /api/analytics/game-states
 *  REST so consumers don't need a second mental model. Emitted on ≥1¢ shift
 *  on either prob; backfilled on subscribe for periodTypeId=1 only. */
export interface HydraConsensus {
  canonicalGameKey: string;
  // Doubleheader-safe key carried alongside the unchanged canonicalGameKey —
  // "sport|YYYY-MM-DD|gameNo|TEAM_LO|TEAM_HI". For a doubleheader both games'
  // consensus frames arrive under the same canonicalGameKey but differ here, so
  // marketStore caches consensus by this key to keep the two games un-blended.
  // Absent only pre-bootstrap (fall back to canonicalGameKey). See HYDRA_API.md.
  canonicalGameKeyV2?: string;
  awayTeam: string;     // Unabated's own away/home — swap when landed in canonicalGameKey's homeCode slot
  homeTeam: string;
  periodTypeId: number; // 1 = full game
  periodLabel: string;
  /** Market discriminator. Absent = the regulation result (1X2 / moneyline) —
   *  the unchanged contract. 'advance' = the soccer knockout "To Advance" 2-way
   *  (who progresses incl. ET + pens), broadcast ADDITIVELY on the SAME
   *  canonicalGameKey + periodTypeId as the result frame, so consumers MUST
   *  bucket by (gameKey, periodTypeId, marketType) or the two clobber each
   *  other. 2-way: away/home only, no draw. See HYDRA_API.md "Soccer To Advance". */
  marketType?: 'advance';
  awayFairProbCents: number;
  homeFairProbCents: number;
  /** Soccer 3-way (Unabated betTypeId 98): the Draw side's fair probability.
   *  Absent/null on binary sports AND on the 'advance' 2-way (it has no draw —
   *  absent, not 0/null). Sides don't sum to 100 minus vig the way a binary pair
   *  does — render each independently. */
  drawFairProbCents?: number | null;
  /** Sub-cent trend feed (since 2026-07-05): fair probabilities at
   *  tenth-of-a-cent resolution — integer tenths 0–1000, sides summing to
   *  exactly 1000, draw soccer-only like the cents. ABSOLUTE values, not
   *  deltas (backpressure coalesces latest-wins) — diff consecutive frames
   *  client-side (lib/consensusTrend). Display surfaces (dashboard Odds
   *  column, panel U-row, PositionTracker) render tenths as the odds when
   *  present, falling back to the integer cents fields above. Optional only
   *  for pre-rollout hydra. */
  awayFairProbTenths?: number;
  homeFairProbTenths?: number;
  drawFairProbTenths?: number | null;
  /** Sharp-anchor flag (since 2026-07-05): true ⇒ Unabated marketSourceId 7
   *  (Pinnacle's sharp feed) anchored this consensus at 4× a field book's
   *  weight; false ⇒ field-only (src 7 had no viable quote or was struck).
   *  A flip alone triggers a frame even with zero numeric change. false is
   *  the NORMAL sparse-source state, not an error. Absent = pre-rollout. */
  sharpBookUsed?: boolean;
  contributingBooks: number;
  lastUpdateMs: number;
}

export interface HydraConsensusMsg {
  type: 'consensus';
  consensus: HydraConsensus;
  serverTs: number;
  seq: number;
}

// ── OddsPapi comparison odds (channel `oddspapi`) ────────────────
// Mirrors hydra's frozen `ServerOpOddsMessage` / `ServerOpSnapshotBegin
// Message` / `ServerOpSnapshotEndMessage` (HYDRA_API.md §"OddsPapi odds").
// Read-only comparison feed beside Unabated — never consensus, never a
// failover, never an execution input.

/** One bookmaker's quote. Decimal odds are STRINGS (hydra's canonical
 *  number→string serialization — contract rule 6); convert for display
 *  only. `*American` fields are the PROVIDER's own conversion — consumers
 *  prefer them and locally convert from the decimal string only when
 *  absent (rule 6b). `drawDecimal` present ⇔ 3-way market (rule 5). */
export interface HydraOpBookQuote {
  bookmaker: string;
  awayDecimal: string;
  homeDecimal: string;
  drawDecimal?: string;
  awayAmerican?: number;
  homeAmerican?: number;
  drawAmerican?: number;
  /** Hydra-computed single-book de-vig (tenths 0–1000, sides sum 1000) —
   *  the de-vigged Pinnacle display line (rule 6c). HYDRA computes fair
   *  values, never this client. Present only on the displayed anchor
   *  book; absence is a renderable state (no secondary line). */
  awayFairTenths?: number;
  homeFairTenths?: number;
  drawFairTenths?: number;
  /** Provider update time (epoch ms) — the per-book freshness source.
   *  Never use the frame's `serverTs` for freshness (rule 7); this
   *  timestamp also governs the de-vigged-Pinnacle secondary AND the
   *  anchor-only state (rule 7 as amended — per-displayed-value). */
  updatedAtMs: number;
}

/** Cross-book de-vigged fair value, hydra-computed by the SAME algorithm
 *  as Unabated's consensus (extracted, not imitated — rule 6c). Absent
 *  ⇔ no computable value exists (mirrors Unabated's low-book behavior);
 *  consumers then render anchor-only, or `—` when the anchor fair value
 *  is also absent. Never computed or adjusted client-side. */
export interface HydraOpConsensus {
  awayFairProbCents: number;
  homeFairProbCents: number;
  /** Soccer 3-way only. */
  drawFairProbCents?: number;
  /** Integer tenths (0–1000), sides sum 1000 — the display precision. */
  awayFairProbTenths: number;
  homeFairProbTenths: number;
  drawFairProbTenths?: number;
  contributingBooks: number;
  /** Analogue of Unabated's sharpBookUsed — pinnacle anchored at elevated
   *  weight; false = field-only (normal sparse state, not an error). */
  pinnacleAnchored: boolean;
  /** THE freshness authority for the consensus primary (rule 7 as
   *  amended); `OpGameOdds.lastUpdateMs` is barred for displayed values. */
  lastUpdateMs: number;
}

/** A previously-quoting book whose price is currently withheld, WITH the
 *  provider's explicit reason (phase 3, rule 12). Producer-side the entry
 *  is never inferred from absence, attribution is contemporaneity-fenced,
 *  and the withhold is debounced (30s sustain) — consumers render it
 *  verbatim and MUST NOT rebuild inference on top of it. `reason` is an
 *  open union ('suspended' | 'stale_link' | 'no_odds' |
 *  'market_inactive' today); unknown values render as generic
 *  "withdrawn" (additive tolerance). `sinceMs` = start of the CONTINUOUS
 *  withhold — the ONLY age authority for the displayed state (rule-7
 *  discipline; serverTs and op.lastUpdateMs barred). */
export interface HydraOpWithdrawnBook {
  bookmaker: string;
  reason: string;
  sinceMs: number;
}

/** Open union — unknown marketType values must be ignored (rule 8). */
export interface HydraOpMarket {
  marketType: string;
  /** Present IFF the market is FULL-GAME (value 1) — full-feed B
   *  (hydracore §"Full-feed B", 2026-08-17): period-scoped entries carry
   *  NO periodTypeId; they carry `period` instead. NOTE the addendum's
   *  precision: `periodTypeId === 1` selects ALL full-game markets
   *  including the new families (team totals, odd/even) — what keeps a
   *  pre-B view rendering its old set is the ignore-unknown-marketType
   *  discipline, not this field. */
  periodTypeId?: number;
  /** Vendor period string, VERBATIM, present IFF the market is NOT
   *  full-game: 'p1+p2+p3+p4+p5' = F5, 'p1+p2' = 1H, 'p3+p4' = 2H,
   *  'pN' = single period/quarter/inning, plus OT combos. The JOIN key
   *  for period-scoped lookups — periodLabel is a display hint only. */
  period?: string;
  periodLabel: string;
  /** THE LINE (hydracore §"Spread & total markets", 2026-08-11; widened
   *  by §"Full-feed B", 2026-08-17). Present IFF the family carries a
   *  line: 'spread' | 'total' | 'teamtotal-home' | 'teamtotal-away' |
   *  'total-touchdowns' | 'total-fieldgoals' — and absent on period
   *  moneylines and odd/even. Spread: the HOME team's handicap
   *  exactly as the provider declares it (home −1.5 ⇒ -1.5; away's implied
   *  handicap is the negation). Total: the over/under line. One line = one
   *  market entry; books never mix across lines and the consensus is
   *  computed among THAT line's books only. Side naming rides the existing
   *  quote fields (sideIndex convention): spread away/home slots are the
   *  away/home sides at that line; total awayDecimal is OVER, homeDecimal
   *  is UNDER. */
  points?: number;
  books: HydraOpBookQuote[];
  consensus?: HydraOpConsensus;
  /** Rule 12 — absolute per-frame state like everything else: presence
   *  lists current withholds, absence means none. No client latching. */
  withdrawnBooks?: HydraOpWithdrawnBook[];
}

export interface HydraOpOdds {
  /** Which comparison feed produced this frame. `'opticodds'` rides the
   *  parallel `opticodds` channel (HYDRA_API.md §"OpticOdds odds" — a
   *  formal delta against the OddsPapi section: identical shapes and
   *  rules, four differences). Rule 10 in both directions: neither
   *  channel ever carries the other's data, and no layer substitutes. */
  provider: 'oddspapi' | 'opticodds';
  /** Provider event id, diagnostics only — consumers route on the
   *  canonical keys. As-built wire carries a NUMBER; workspace §5 says
   *  string (G1-DNL-002) — typed tolerant, never consumed. */
  fixtureId: number | string;
  canonicalGameKey: string;
  canonicalGameKeyV2: string;
  sport: string;
  /** Provider's own orientation mapped to canonical codes — align to the
   *  row exactly as for consensus (`awayTeam === row.homeCode` ⇒ swapped,
   *  rule 4). */
  awayTeam: string;
  homeTeam: string;
  lastUpdateMs: number;
  /** ABSOLUTE per-game state; `[]` is an explicit no-coverage TOMBSTONE —
   *  delete the entry (rule 1). */
  markets: HydraOpMarket[];
}

export interface HydraOpOddsMsg {
  type: 'opOdds';
  /** Legacy 4-seg canonicalGameKey (top-level: per-game coalesce key +
   *  tickers[] filter). Bucket by `op.canonicalGameKeyV2` (rule 3). */
  ticker: string;
  sport: string;
  /** Present ⇔ the frame belongs to a snapshot burst. */
  generation?: number;
  /** Opaque producer-incarnation id — HYDRA's process, not the provider's
   *  gateway `serverEpoch` (unrelated; never on this wire). Equality-
   *  compared only (rule 2e). */
  sourceEpoch: string;
  /** Per-game source revision, monotonic within one `sourceEpoch` — the
   *  causal-ordering authority; arrival order is never trusted (rule 2b). */
  rev: number;
  op: HydraOpOdds;
  serverTs: number;
  seq: number;
}

export interface HydraOpSnapshotBeginMsg {
  type: 'opSnapshotBegin';
  /** Present (as 'opticodds') ONLY on the opticodds channel — Begin/End
   *  carry no `op` block, so this is how a socket subscribed to both
   *  channels tells the bursts apart. Absent ⇒ oddspapi (frozen fixture
   *  parity). Mirrored onto the worker's SYNTHESIZED bursts so tabs can
   *  route them the same way. */
  provider?: 'opticodds';
  generation: number;
  sourceEpoch: string;
  serverTs: number;
  seq: number;
}

export interface HydraOpSnapshotEndMsg {
  type: 'opSnapshotEnd';
  /** See HydraOpSnapshotBeginMsg.provider. */
  provider?: 'opticodds';
  generation: number;
  sourceEpoch: string;
  /** Frames sent in the burst — the consumer's integrity check (rule 2). */
  gameCount: number;
  serverTs: number;
  seq: number;
}

/** Active-sport set meta frame (channel `markets`, HYDRA_API.md §Active
 *  sports). Sorted uppercase hydra wire codes for every sport with ≥1
 *  currently-live market. Sent on every markets/all subscribe (before any
 *  market frame) and re-broadcast on set change. Each frame is the complete
 *  replacement set — never a delta — and is NOT scoped by the consumer's
 *  sports[] filter (discovering sports outside the filter is the point). */
export interface HydraActiveSportsMsg {
  type: 'activeSports';
  sports: string[];
  serverTs: number;
  seq?: number;
}

type HydraServerMsg =
  | HydraMarketMsg
  | HydraOrderbookMsg
  | HydraConsensusMsg
  | HydraActiveSportsMsg
  | HydraFilterAckMsg
  | HydraOpOddsMsg
  | HydraOpSnapshotBeginMsg
  | HydraOpSnapshotEndMsg
  | { type: 'pong'; clientTs: number; serverTs: number }
  | { type: 'backpressure'; level: number; pending: number; message: string }
  | { type: 'error'; message: string };

// Internal control messages from the SharedWorker.
type WorkerControlMsg = { type: '__status'; connected: boolean } | FilterResetMsg | { type: '__alive'; at: number };

// ── Callbacks ────────────────────────────────────────────────────

export interface HydraCallbacks {
  onMarket: (market: HydraMarket) => void;
  onOrderbook: (ticker: string, book: HydraOrderbook) => void;
  /** `serverTs` is the broadcast time of the frame — on a live update it
   *  equals `consensus.lastUpdateMs`; on a snapshot-burst re-delivery it can
   *  be minutes later. The gap dates trend/recency state (a stale snapshot
   *  must seed dim, not flash). */
  onConsensus: (consensus: HydraConsensus, serverTs?: number) => void;
  /** Complete replacement set of sports with ≥1 live market (uppercase wire
   *  codes; may include codes g1 doesn't render — consumer filters). Fires
   *  on every (re)subscribe and on set change. */
  onActiveSports?: (sports: string[]) => void;
  onStatus: (connected: boolean) => void;
  /** Fires on EVERY server frame, pong and control frames included —
   *  "the transport is alive", regardless of whether any market data
   *  changed. Drives the feed dot's quiet-vs-stalled distinction. Keep
   *  the handler trivially cheap (it can fire hundreds of times/sec). */
  onKeepalive?: () => void;
  /** Latest hydra backpressure level for this connection (0–3). At level
   *  >= 2 hydra suspends orderbook frames entirely (only market + consensus
   *  flow, coalesced) — orderbook-driven UI should show a stale indicator.
   *  Hydra only messages upward transitions; recovery is signalled by
   *  orderbook frames resuming, at which point this fires with 0. */
  onBackpressure?: (level: number) => void;
  /** OddsPapi frames (raw wire messages — the caller's opSnapshotStager
   *  owns staging/commit/rev semantics). Dispatched ONLY on a client
   *  constructed with `subscribeOpOdds: true` (the firehose — DNL-003);
   *  on any other client the frames are dropped + counted, never
   *  dispatched. In shared mode these arrive worker-mediated: live frames
   *  relayed verbatim, snapshot bursts re-framed by the worker (its own
   *  generation namespace) — same stager semantics either way. */
  onOpOdds?: (msg: HydraOpOddsMsg) => void;
  onOpSnapshotBegin?: (msg: HydraOpSnapshotBeginMsg) => void;
  onOpSnapshotEnd?: (msg: HydraOpSnapshotEndMsg) => void;
  /** Consumer feed filters (Stage C). `onFilterReset` is the ONE atomic reset
   *  per installed generation — fired BEFORE any frame of that generation
   *  reaches `onMarket` (shared mode: the worker emits it; direct mode: this
   *  client does, on its own ack). The receiver drops every market row that
   *  fails `scope` (rowPassesFilter); the replacement snapshot repopulates.
   *  `onFilterAck` is telemetry (health panel / verification) — every ack,
   *  accepted or rejected, on both transports. */
  onFilterReset?: (reset: FencePair & { scope: MarketFilterEffective }) => void;
  onFilterAck?: (ack: HydraFilterAckMsg) => void;
}

// ── Constants ────────────────────────────────────────────────────

const PING_INTERVAL = 30_000;
const BASE_RECONNECT_DELAY = 1_000;
const MAX_RECONNECT_DELAY = 30_000;

// ── Client ───────────────────────────────────────────────────────

// ── SharedWorker generation (the wedge escape) ───────────────────
//
// `new SharedWorker(name)` ATTACHES to any existing instance with that name —
// it never replaces one. So when a worker instance goes irrecoverably sick
// (its global scope crashed during an outage storm, say), every recovery path
// re-adopts the corpse: restartShared() spawns "a new worker" that IS the old
// one, a reload attaches to it, a new tab attaches to it. The only escapes
// were closing every tab in the partition or killing the browser process —
// verified live 2026-08-16, where a wedged fleet survived reloads AND a
// browser "close" (background mode kept the process).
//
// The escape: version the instance name with a GENERATION persisted per base
// name. Generation 0 is the bare base name, so the default path — every
// browser that never needed a restart — is byte-identical to before.
//
// The LIFECYCLE BOUNDARY matters as much as the mechanism (DNL-R13-P1: the
// first cut escaped on every restartShared, turning bfcache restores and
// quiet-feed stalls into a generation mint). The states are kept distinct:
//
//   port recovery  — bfcache restore, tab-side stall — reattaches at the
//                    SAME generation, exactly the pre-escape behavior.
//   proven sick    — a fresh attach yields ZERO port messages for
//                    GREET_TIMEOUT_MS. The worker re-broadcasts `__status`
//                    to every new port regardless of market quiet, so total
//                    attach silence is evidence about the WORKER GLOBAL,
//                    not the feed. First timeout retries the current
//                    generation once; a second silent attach escapes
//                    (adopt-or-bump).
//   convergence    — peers observe the generation write via the `storage`
//                    event and migrate even while healthy, so the abandoned
//                    instance loses ALL its ports (bounded double-socket:
//                    storage events deliver in milliseconds, not "whenever
//                    the laggard's own detector happens to fire").
//
// Storage failures degrade to the bare base name in persistence while still
// escaping in memory, never to a crash.
const WORKER_GEN_STORAGE_PREFIX = 'g1xl_worker_gen:';

function readWorkerGeneration(baseName: string): number {
  try {
    const raw = localStorage.getItem(WORKER_GEN_STORAGE_PREFIX + baseName);
    if (raw == null) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeWorkerGeneration(baseName: string, gen: number): void {
  try {
    localStorage.setItem(WORKER_GEN_STORAGE_PREFIX + baseName, String(gen));
  } catch { /* storage unavailable — the bare name still works */ }
}

/** Gen 0 MUST be the bare base name: browsers that never restart stay on the
 *  exact instance names prod has always used. */
function decoratedWorkerName(baseName: string, gen: number): string {
  return gen > 0 ? `${baseName}#g${gen}` : baseName;
}

export class HydraClient {
  private url: string;
  private callbacks: HydraCallbacks;
  // SharedWorker instance name. Each live feed (firehose, edge:nfl, …) MUST use
  // a distinct name so they get separate worker instances / upstream sockets;
  // two clients sharing a name would share one worker (and one URL).
  // `workerName` is the CURRENT effective (generation-decorated) name;
  // `workerBaseName` is the stable identity config supplied.
  private workerName: string;
  private readonly workerBaseName: string;
  private workerGen: number;
  // Proven-sickness detector: armed on EVERY shared attach, cleared by ANY
  // port message (the worker's own greet counts — it is upstream-independent
  // proof the global runs). Fires ⇒ retry current generation once, then
  // escape. See the generation doc block above the class.
  private greetTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private greetTimeoutRetriedCurrentGen = false;
  private readonly GREET_TIMEOUT_MS = 10_000;
  // Peer-escape observation (`storage` fires only for OTHER tabs' writes).
  private storageListener: ((ev: StorageEvent) => void) | null = null;

  // Direct-WS path
  private ws: WebSocket | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;

  // SharedWorker path
  private worker: SharedWorker | null = null;
  private port: MessagePort | null = null;
  private workerHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private stallDetectorTimer: ReturnType<typeof setInterval> | null = null;
  // Liveness ping cadence for the SharedWorker reaper. Worker drops
  // sources stale > 30 s; 10 s here gives 3 attempts before reap.
  private readonly WORKER_HEARTBEAT_INTERVAL_MS = 10_000;
  // Tab-side stall detector: if the port goes quiet for this long
  // while we still think we're connected, the worker has stopped
  // talking to us (port reaped during freeze, worker died, etc).
  // Recovery is stop()+startShared() — same path as a bfcache resume.
  //
  // 5 s threshold is aggressive but matches normal traffic: healthy
  // tallies routinely show 100+ frames per 5 s window. Caveat: during
  // truly dead market hours, only the 30 s pong rebroadcast keeps the
  // tab "alive," and 5 s would false-trigger restarts every 5 s; bump
  // to 10–15 s if that ever bites. Check interval 2 s caps detection
  // latency at threshold + interval = ~7 s worst case.
  private readonly TAB_STALL_THRESHOLD_MS = 5_000;
  private readonly TAB_STALL_CHECK_INTERVAL_MS = 2_000;

  // Cached orderbook needs (shared mode only). When stop()+start()
  // rebuilds the SharedWorker port — bfcache resume, stall recovery —
  // we replay these to the new port without waiting for components to
  // re-mount. Worker also caches per-port as belt-and-suspenders.
  private lastDeclaredNeeds: string[] = [];

  private mode: 'shared' | 'direct' | null = null;
  private stopped = false;
  private _connected = false;
  private _connectedSinceMs = 0;

  /**
   * The ONLY place `_connected` changes, so a connection boundary can never be
   * handled at five sites and missed at the sixth (DNL-001 was exactly that).
   * On a real transition it clears the OP proof-of-service stamp: liveness is a
   * property of the CURRENT transport, and a timestamp that outlives its socket
   * lets a reconnected-but-mute engine look like it is still serving.
   *
   * Idempotent on a repeated same-value status — the SharedWorker re-broadcasts
   * `__status` (greet/replay), and treating those as boundaries would erase a
   * live engine's proof over and over so it could never be promoted.
   */
  private setConnectedState(next: boolean): void {
    if (this._connected === next) return;
    this._connected = next;
    this._lastOpFrameMs = 0;
    this._connectedSinceMs = next ? Date.now() : 0;
  }

  // Latest backpressure level hydra has reported for this connection
  // (0–3). At >= 2 hydra suspends orderbook frames — see HYDRA_API.md
  // "Backpressure warning". Cleared back to 0 when an orderbook frame
  // arrives (the recovery signal — hydra sends no "recovered" message)
  // or when the connection drops (fresh socket = empty buffer = level 0).
  private _backpressureLevel = 0;

  // Stats
  private _marketsReceived = 0;
  private _orderbooksReceived = 0;
  private _lastMessageMs = 0;
  /** Direct-mode market frames dropped by the fence (stale pair / unstamped). */
  private _marketFramesDropped = 0;

  // OddsPapi capability flag (DNL-003, failover rev 2026-08-11). Set on the
  // env-preferred OP source and the OG warm standby; a client without it
  // DROPS oddspapi-channel frames (counted below) instead of dispatching
  // them. Exactly-one-ingestion is enforced a layer up: useHydra gates
  // dispatched frames to the single ACTIVE source, so a capable standby's
  // frames never reach the stager while the preferred source serves.
  private readonly subscribeOpOdds: boolean;
  /** OpticOdds trial channel — subscribed alongside oddspapi on the SAME
   *  socket, but only where the upstream speaks it (hydracore). Never set
   *  for the OG firehose: OG's zod enum rejects the channel with an error
   *  frame per attempt (harmless but noise, and contractually pointless). */
  private readonly subscribeOoOdds: boolean;
  // Dedicated OP transport (feedConfig `op:0`): this connection speaks ONLY
  // the oddspapi channel — markets/unabated subscribes are skipped in both
  // transports (worker via __init, direct below).
  private readonly opOnly: boolean;
  private _opFramesDropped = 0;
  // Wall-clock of the last oddspapi-channel frame DISPATCHED by this client
  // (0 = none ever). This is the only per-CHANNEL liveness signal in the
  // codebase: `lastMessageMs` is channel-agnostic and a 30s pong keeps it warm
  // forever, and the worker's stall watchdog deliberately excludes OP frames.
  // Without this, "connected but no longer serving oddspapi" is invisible — a
  // core whose OP lane dies with its socket up looks perfectly healthy to
  // every other mechanism. useHydra's ranked failover reads it to decide both
  // demotion and — critically — whether a better-ranked core has PROVEN it
  // serves the channel before being promoted back into the authority slot.
  private _lastOpFrameMs = 0;

  // Current orderbook-channel subscription state (direct-WS mode only;
  // SharedWorker path delegates this to hydraWorker.ts via __needs).
  private directOrderbookNeeds: Set<string> = new Set();
  /** Direct-WS props opt-in (mirrors the worker's sticky propsWanted). */
  private directPropsWanted = false;
  private directUpstreamOrderbookTickers: Set<string> = new Set();
  private directPendingUnsubscribeTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private readonly UNSUBSCRIBE_GRACE_MS = 30_000;

  // ── Consumer feed filters (Stage C) ─────────────────────────────
  // Rollout mode, resolved once per client (env is build-time static) and
  // shipped to the SharedWorker on `__init` — the worker never reads env.
  private readonly filterMode: FeedFilterMode;
  // Uppercase wire codes of the sports THIS feed may own (its claim scope);
  // null = unrestricted (the firehose's complement is dynamic, so it passes
  // null and the Phase-2 sports axis is restricted by the tab-side needs
  // publisher instead). Ships to the worker on `__init` as well.
  private readonly feedSports: ReadonlySet<string> | null;
  // Cached needs (shared mode): replayed to a rebuilt port exactly like
  // lastDeclaredNeeds, so a stall recovery / bfcache reattach keeps the same
  // filter without waiting for views to re-declare.
  private lastDeclaredFilterNeeds: FeedNeeds | null = null;
  /** Requested cadence in force for this connection (mode default until the
   *  fleet re-negotiates it — the core floor's standby/sole-source switch). */
  private policyCadenceMs: number;
  // Direct-WS path only — the SharedWorker owns these in shared mode.
  private directFilterAggregator: FeedFilterAggregator<'tab'> | null = null;
  private directFilter: MarketFilter | null = null;
  private readonly directFence = new FilterFence();
  /** True while the connect-time subscribe folds pending needs in: the
   *  aggregator's change callback must update the filter but NOT send a
   *  replace — the connect op that follows carries it. */
  private directSubscribing = false;
  /** First-subscribe deferral (mirror of the worker's): a socket opened
   *  before any view declared waits up to FIRST_NEEDS_WAIT_MS for the
   *  declaration rather than pulling the whole slate broad and replacing it
   *  50 ms later. */
  private deferredMarketsSubscribe: ReturnType<typeof setTimeout> | null = null;
  private static readonly FIRST_NEEDS_WAIT_MS = 300;
  /** Ack-timeout fallback (mirror of the worker's): a filtered subscribe that
   *  gets no filterAck within ACK_TIMEOUT_MS degrades THIS socket to the
   *  legacy subscribe — broad and admitted — instead of dark. Retried on
   *  every new socket; a late ack re-arms the fence. */
  private static readonly ACK_TIMEOUT_MS = 8_000;
  private directAckTimer: ReturnType<typeof setTimeout> | null = null;
  private directLegacyFallback = false;

  constructor(
    url: string,
    callbacks: HydraCallbacks,
    opts: {
      workerName?: string; subscribeOpOdds?: boolean; subscribeOoOdds?: boolean; opOnly?: boolean;
      /** Override the env-resolved rollout mode (tests). */
      feedFilterMode?: FeedFilterMode;
      /** Sports this feed may own (any case; normalized to wire codes). */
      feedSports?: Iterable<string> | null;
    } = {},
  ) {
    this.url = url;
    this.callbacks = callbacks;
    this.filterMode = opts.feedFilterMode ?? resolveFeedFilterMode();
    this.policyCadenceMs = this.filterMode.cadenceMs ?? 250;
    this.feedSports = opts.feedSports ? new Set([...opts.feedSports].map((s) => s.toUpperCase())) : null;
    this.workerBaseName = opts.workerName ?? 'hydra-shared';
    // Join the fleet's LATEST generation at construction: if another tab
    // already escaped a wedged instance, a fresh page load must land on the
    // healthy replacement, not re-adopt the corpse by its old name.
    this.workerGen = readWorkerGeneration(this.workerBaseName);
    this.workerName = decoratedWorkerName(this.workerBaseName, this.workerGen);
    this.subscribeOpOdds = opts.subscribeOpOdds ?? false;
    this.subscribeOoOdds = opts.subscribeOoOdds ?? false;
    this.opOnly = opts.opOnly ?? false;
  }

  get connected(): boolean { return this._connected; }
  get marketsReceived(): number { return this._marketsReceived; }
  get marketFramesDropped(): number { return this._marketFramesDropped; }
  get orderbooksReceived(): number { return this._orderbooksReceived; }
  get lastMessageMs(): number { return this._lastMessageMs; }
  get backpressureLevel(): number { return this._backpressureLevel; }
  /** oddspapi-channel frames dropped because this client is not the OP
   *  ingestion authority (`subscribeOpOdds: false`) — the DNL-003 counted
   *  diagnostic. */
  get opFramesDropped(): number { return this._opFramesDropped; }
  /** Wall-clock of the last oddspapi frame this client dispatched ON THE
   *  CURRENT CONNECTION; 0 = none since the last connection boundary.
   *  Per-channel liveness for the ranked OP failover — see `_lastOpFrameMs`. */
  get lastOpFrameMs(): number { return this._lastOpFrameMs; }
  /** Wall-clock of the current connection's establishment; 0 while down. Anchors
   *  the not-yet-delivered grace so a reconnect gets a fresh window instead of
   *  inheriting a tenure clock that already expired. */
  get connectedSinceMs(): number { return this._connectedSinceMs; }

  /** Transport this client resolved to: 'shared' (SharedWorker — one upstream WS
   *  shared across all tabs) or 'direct' (a per-window WS, the fallback where
   *  `type:'module'` SharedWorkers are unsupported, e.g. Safari); 'connecting'
   *  before start / after stop. Surfaced in G1 Health so the shared-vs-direct
   *  question (the "new connection every reload/window" symptom) is observable. */
  get transport(): 'shared' | 'direct' | 'connecting' {
    return this.mode ?? 'connecting';
  }

  private setBackpressureLevel(level: number): void {
    if (level === this._backpressureLevel) return;
    this._backpressureLevel = level;
    this.callbacks.onBackpressure?.(level);
  }

  /** Declare the set of tickers this view needs orderbook data for. In
   *  SharedWorker mode, forwarded to the worker which unions across all
   *  tabs. In direct-WS mode, reconciled locally against the current
   *  upstream subscription with a 30 s grace on removals. */
  setOrderbookNeeds(tickers: string[]): void {
    if (this.mode === 'shared') {
      // Cache before posting so a future startShared() (bfcache resume,
      // stall recovery) can replay the same needs without depending on
      // component remounts.
      this.lastDeclaredNeeds = tickers.slice();
      if (this.port) {
        try {
          this.port.postMessage({ type: '__needs', channel: 'orderbooks', tickers });
        } catch { /* port may be closing — next mount will re-declare */ }
      }
      return;
    }
    // Direct-WS: this client is the sole consumer, so needs == desired.
    this.directOrderbookNeeds = new Set(tickers);
    this.reconcileDirectOrderbookSubscriptions();
  }

  /** Declare this tab's aggregated feed-filter needs (which normally-excluded
   *  classes/types it renders; Phase 2: which sports). Shared mode forwards to
   *  the worker, which unions across every attached tab and re-sends the
   *  complete filter (replace semantics) when the union changes. Direct mode
   *  aggregates locally and re-sends on this socket. Inert when the rollout
   *  flag is off. */
  setMarketFilterNeeds(needs: FeedNeeds | null): void {
    if (!this.filterMode.enabled || this.opOnly) return;
    // Cached first: a client that has not started yet (or is between
    // transports) replays this on startShared / the direct connect.
    this.lastDeclaredFilterNeeds = needs;
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__filter_needs', needs: serializeNeeds(needs) });
        } catch { /* port may be closing — next mount will re-declare */ }
      }
    } else if (this.mode === 'direct') {
      this.ensureDirectFilterAggregator().setNeeds('tab', needs);
      if (this.deferredMarketsSubscribe) {
        // The first declaration IS the first subscribe.
        clearTimeout(this.deferredMarketsSubscribe);
        this.deferredMarketsSubscribe = null;
        this.subscribeMarketsChannel();
      }
    }
  }

  /** Connection-wide requested cadence (the two-tier model: the core floor
   *  runs 1000 as the warm standby and 250 while it is the only source for a
   *  viewed sport). Re-sends the complete filter when it changes. Inert
   *  with the flag off. */
  setMarketFilterPolicy(policy: { cadenceMs: number }): void {
    if (!this.filterMode.enabled || this.opOnly) return;
    if (!Number.isFinite(policy.cadenceMs) || policy.cadenceMs === this.policyCadenceMs) return;
    this.policyCadenceMs = policy.cadenceMs;
    if (this.mode === 'shared') {
      if (this.port) {
        try { this.port.postMessage({ type: '__filter_policy', cadenceMs: policy.cadenceMs }); } catch { /* port closing */ }
      }
      return;
    }
    if (this.mode === 'direct' && this.directFilterAggregator) {
      const next = buildMarketFilter(this.directFilterAggregator.current(), {
        sportsAxis: this.filterMode.sportsAxis, feedSports: this.feedSports, cadenceMs: this.policyCadenceMs,
      });
      if (this.directFilter && filterKey(this.directFilter) === filterKey(next)) return;
      this.directFilter = next;
      if (!this.deferredMarketsSubscribe && !this.directLegacyFallback && this.ws && this.ws.readyState === WebSocket.OPEN) this.send(replaceMarketFilterOp(next));
    }
  }

  /** The filter currently in force on the direct socket (null = legacy /
   *  shared mode / flag off). Diagnostics + tests. */
  get directMarketFilter(): MarketFilter | null {
    return this.directFilter;
  }

  /** The direct socket's installed fencing pair (null until its ack). */
  get directFencePair(): FencePair | null {
    return this.directFence.pair;
  }

  /** True while the direct socket runs the legacy subscribe because its
   *  engine never acked the filter (diagnostics + tests). */
  get directLegacyFallbackActive(): boolean {
    return this.directLegacyFallback;
  }

  private clearDirectAckTimer(): void {
    if (this.directAckTimer) { clearTimeout(this.directAckTimer); this.directAckTimer = null; }
  }

  private degradeDirectToLegacy(reason: string): void {
    if (this.directLegacyFallback || this.directFence.pair) return;
    this.directLegacyFallback = true;
    console.warn(`[Hydra] markets filter unsupported on ${this.url} (${reason}) — falling back to the legacy subscribe on this socket`);
    for (const op of connectMarketOps(null, this.directPropsWanted)) this.send(op);
  }

  private ensureDirectFilterAggregator(): FeedFilterAggregator<'tab'> {
    if (this.directFilterAggregator) return this.directFilterAggregator;
    this.directFilterAggregator = new FeedFilterAggregator<'tab'>((needs) => {
      const next = buildMarketFilter(needs, {
        sportsAxis: this.filterMode.sportsAxis,
        feedSports: this.feedSports,
        cadenceMs: this.policyCadenceMs,
      });
      if (this.directFilter && filterKey(this.directFilter) === filterKey(next)) return;
      this.directFilter = next;
      // Complete replacement on the live socket; a closed socket picks the
      // current filter up on its next onopen (connect-time carry). A
      // degraded (legacy-fallback) socket picks it up on its next connect.
      if (!this.directSubscribing && !this.directLegacyFallback && this.ws && this.ws.readyState === WebSocket.OPEN) this.send(replaceMarketFilterOp(next));
    });
    // Seed the connection's filter from empty needs (defaults) synchronously
    // so the FIRST subscribe already carries it — never a legacy connect
    // followed by a narrowing replace.
    this.directFilter = buildMarketFilter(this.directFilterAggregator.current(), {
      sportsAxis: this.filterMode.sportsAxis,
      feedSports: this.feedSports,
      cadenceMs: this.policyCadenceMs,
    });
    return this.directFilterAggregator;
  }

  start(): void {
    this.stopped = false;
    if (typeof SharedWorker !== 'undefined') {
      if (this.startShared()) return;
    }
    this.connect();
  }

  /** Re-request a markets snapshot from upstream. Used by `useHydra` when
   *  the tab returns from background (visibilitychange) or the watchdog
   *  detects a silent stall — both cases where the WS thinks it's
   *  connected but `lastMessageMs` has gone quiet. In SharedWorker mode
   *  delegates to the worker, which forwards to upstream; cache entries
   *  get overwritten as frames stream in. */
  requestResnapshot(): void {
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__resnapshot' });
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ op: 'snapshot', channel: 'markets' });
    }
    // If not connected, the next reconnect's onopen will snapshot anyway,
    // so no extra work needed here.
  }

  /** A props surface opened: tell the shared worker to stop discarding prop
   *  market frames (sticky for the worker's lifetime) and backfill via a
   *  markets resnapshot. Direct-WS mode never discards, so this is a no-op
   *  there — the frames were already flowing. */
  requestPropsFeed(): void {
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__wantProps' });
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    // Direct-WS: the engine-side excludeMarketTypes filter applies here too
    // (connectMarketOps default), so lift it the same way — resubscribe
    // without the field + snapshot. Sticky across reconnects.
    this.directPropsWanted = true;
    // Flag on: the versioned filter governs this socket (never-mix) and the
    // props surface's useFeedNeeds declaration is what widens it.
    if (this.filterMode.enabled) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      for (const op of connectMarketOps(null, true)) this.send(op);
    }
  }

  /** Re-request an `oddspapi` snapshot burst — the stager's bounded-backoff
   *  recovery path (truncated burst, unknown epoch). No-op on a client that
   *  is not the OP ingestion authority. Shared mode delegates to the worker
   *  (which throttles duplicate requests across tabs); direct mode sends
   *  the snapshot op itself — a disconnected socket needs nothing extra,
   *  the reconnect onopen re-snapshots every channel. */
  requestOpResnapshot(): void {
    if (!this.subscribeOpOdds) return;
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__op_resnapshot' });
        } catch { /* port may be closing — reconnect path re-snapshots */ }
      }
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ op: 'snapshot', channel: 'oddspapi' });
    }
  }

  /** OpticOdds twin of requestOpResnapshot — same recovery path, its own
   *  channel + worker message so the two lanes' throttles stay independent. */
  requestOoResnapshot(): void {
    if (!this.subscribeOoOdds) return;
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__oo_resnapshot' });
        } catch { /* port may be closing — reconnect path re-snapshots */ }
      }
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.send({ op: 'snapshot', channel: 'opticodds' });
    }
  }

  /** Force a full WS reconnect — used when the freshness signal is
   *  yellow AND the user took an explicit action (chip click, button
   *  press) that says "I want fresh data NOW." Recovers from silently
   *  dead sockets where requestResnapshot() over the same dead socket
   *  is itself a lost message.
   *
   *  Shared mode: posts __reconnect_upstream so the worker rebuilds
   *  the upstream WS for every attached tab. Direct mode: stops then
   *  immediately restarts the per-tab WS. Both paths bypass any
   *  staleness gate — the caller already decided the feed is stale. */
  forceReconnect(): void {
    if (this.mode === 'shared') {
      if (this.port) {
        try {
          this.port.postMessage({ type: '__reconnect_upstream' });
        } catch { /* port may be closing — next mount will resume */ }
      }
      return;
    }
    // Direct mode: tear down and immediately re-open. clearTimers +
    // explicit close keeps the existing onclose path quiet enough that
    // we don't double-schedule a reconnect; the new connect() lands
    // before the backoff timer would have.
    this.clearTimers();
    if (this.ws) {
      try { this.ws.close(); } catch { /* ignore */ }
      this.ws = null;
    }
    this.setConnectedState(false);
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.mode === 'shared') {
      this.stopWorkerHeartbeat();
      this.stopStallDetector();
      this.clearGreetTimeout();
      this.removeStorageListener();
      if (this.port) {
        try { this.port.postMessage({ type: '__bye' }); } catch { /* port may be closed */ }
        try { this.port.close(); } catch { /* ignore */ }
        this.port = null;
      }
      this.worker = null;
    } else {
      this.clearTimers();
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
    }
    this.mode = null;
    this.setConnectedState(false);
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
  }

  // ── SharedWorker path ──────────────────────────────────────────

  private startShared(): boolean {
    try {
      this.worker = new SharedWorker(
        new URL('../workers/hydraWorker.ts', import.meta.url),
        { type: 'module', name: this.workerName },
      );
      this.port = this.worker.port;
      this.port.onmessage = (ev: MessageEvent) => {
        const msg = ev.data as HydraServerMsg | WorkerControlMsg | null;
        if (!msg || typeof msg !== 'object') return;
        this._lastMessageMs = Date.now();
        // Any port message is proof the worker global runs — the sickness
        // detector stands down and the retry ledger resets.
        this.clearGreetTimeout();
        this.greetTimeoutRetriedCurrentGen = false;
        if (msg.type === '__status') {
          this.setConnectedState(!!msg.connected);
          // A status flip means the upstream socket changed — any prior
          // backpressure level belonged to the old socket. (If still
          // throttled, the worker's greet() replays the cached
          // backpressure message right after.)
          this.setBackpressureLevel(0);
          this.callbacks.onStatus(this._connected);
          return;
        }
        if (msg.type === '__alive') return; // liveness only — _lastMessageMs was bumped above
        if (msg.type === '__filterReset') {
          // The worker already fenced the upstream; this is the atomic
          // reset for the tab's stores, ordered by the port ahead of every
          // post-fence frame (worker: reset broadcast precedes forwarding).
          this.callbacks.onFilterReset?.({
            socketIncarnation: msg.socketIncarnation,
            filterGeneration: msg.filterGeneration,
            scope: msg.scope,
          });
          return;
        }
        this.handleMessage(msg);
      };
      this.port.start();
      // subscribeOpOdds / opOnly ride __init so the worker knows whether ITS
      // upstream should subscribe the oddspapi channel, and whether it is a
      // dedicated OP transport (skip markets/unabated entirely) — feed-unique
      // workerName keeps the flags coherent across every tab of one feed.
      this.port.postMessage({
        type: '__init', url: this.url, subscribeOpOdds: this.subscribeOpOdds,
        subscribeOoOdds: this.subscribeOoOdds,
        opOnly: this.opOnly,
        // Consumer feed filters (Stage C): the rollout mode + this feed's
        // claim scope latch per worker name exactly like opOnly (all tabs of
        // one feed pass the same values).
        feedFilter: this.filterMode,
        feedSports: this.feedSports ? [...this.feedSports] : null,
      });
      // Replay cached orderbook needs if any. Happens on stop()+start()
      // recovery (bfcache resume, stall detector) so the new port gets
      // the same subscriptions the old port had without waiting for
      // components to re-mount.
      if (this.lastDeclaredNeeds.length > 0) {
        try {
          this.port.postMessage({
            type: '__needs', channel: 'orderbooks', tickers: this.lastDeclaredNeeds.slice(),
          });
        } catch { /* port may be closing — extremely unlikely on a just-created port */ }
      }
      if (this.filterMode.enabled && this.lastDeclaredFilterNeeds != null) {
        try {
          this.port.postMessage({ type: '__filter_needs', needs: serializeNeeds(this.lastDeclaredFilterNeeds) });
        } catch { /* same as above */ }
      }
      if (this.filterMode.enabled && this.policyCadenceMs !== (this.filterMode.cadenceMs ?? 250)) {
        try { this.port.postMessage({ type: '__filter_policy', cadenceMs: this.policyCadenceMs }); } catch { /* same */ }
      }
      this.startWorkerHeartbeat();
      this.startStallDetector();
      this.armGreetTimeout();
      this.installStorageListener();
      // Reset the freshness clock so the stall detector gives the new
      // connection a full TAB_STALL_THRESHOLD_MS to deliver its first
      // frame instead of inheriting the stale value from before.
      this._lastMessageMs = Date.now();
      this.mode = 'shared';
      console.log(`[Hydra:${this.workerName}] using SharedWorker (one upstream WS shared across all tabs) → ${this.url}`);
      return true;
    } catch (err) {
      console.warn('[Hydra] SharedWorker init failed, falling back to direct WS:', err);
      this.worker = null;
      this.port = null;
      return false;
    }
  }

  /** Tear down and recreate the SharedWorker port at the CURRENT generation.
   *  Port recovery, not instance escape (DNL-R13-P1): "my port may need
   *  reattachment" and "this named worker is irrecoverably sick" are
   *  different states, and conflating them turned every bfcache restore and
   *  quiet-feed stall into a generation bump — a connection-churn generator.
   *  Same-generation reattach is the cheap pre-escape behavior; the ONLY
   *  path that changes generation is the greet-timeout (proven sickness:
   *  a fresh attach that cannot even produce the worker's own `__status`
   *  greet means the worker global is not running). */
  restartShared(reason: string): void {
    if (this.mode !== 'shared' && this.mode !== null) return;
    // The stall restart IS the "first retry the current generation" step:
    // if the replacement attach also stays mute, the greet-timeout escapes.
    this.greetTimeoutRetriedCurrentGen = true;
    console.warn(`[Hydra] tab-side restart: ${reason} → reattaching '${this.workerName}' (gen ${this.workerGen})`);
    this.teardownSharedPort();
    this.startShared();
  }

  /** bfcache-restore reattach: same generation, and the retry ledger is
   *  RESET — a freeze explains any pre-freeze silence, so the restored tab
   *  gets a fresh proof window instead of inheriting a stall streak that
   *  would escape on its first timeout (DNL-R13-P1: a back/forward
   *  restoration must never abandon a healthy worker). */
  reattachShared(reason: string): void {
    if (this.mode !== 'shared' && this.mode !== null) return;
    this.greetTimeoutRetriedCurrentGen = false;
    console.warn(`[Hydra] bfcache reattach: ${reason} → '${this.workerName}' (gen ${this.workerGen})`);
    this.teardownSharedPort();
    this.startShared();
  }

  /** The ONE path that changes generation. Reached only from the
   *  greet-timeout: a fresh attach produced ZERO port messages — not even
   *  the `__status` greet the worker re-broadcasts to every new port — for
   *  GREET_TIMEOUT_MS. Market quiet cannot cause that (the greet is
   *  upstream-independent); only a worker global that is not running can.
   *  Adopt a newer stored generation if another tab already escaped;
   *  otherwise bump. Concurrent bumps compute the same number and converge. */
  private escapeShared(reason: string): void {
    const stored = readWorkerGeneration(this.workerBaseName);
    if (stored > this.workerGen) {
      this.workerGen = stored;
    } else {
      this.workerGen += 1;
      writeWorkerGeneration(this.workerBaseName, this.workerGen);
    }
    this.workerName = decoratedWorkerName(this.workerBaseName, this.workerGen);
    this.greetTimeoutRetriedCurrentGen = false; // new instance earns a fresh retry
    console.warn(`[Hydra] worker escape: ${reason} → spawning '${this.workerName}' (gen ${this.workerGen})`);
    this.teardownSharedPort();
    this.startShared();
  }

  /** A peer tab escaped to a newer generation (its storage write fired our
   *  `storage` listener). Migrate even while healthy: without this, tabs
   *  whose port to the OLD instance is fine never observe the write and
   *  keep the abandoned instance's upstream socket alive indefinitely —
   *  the unbounded double-socket Danielle flagged (DNL-R13-P1). Adopt-only:
   *  never bumps, never writes. */
  private migrateToGeneration(gen: number, source: string): void {
    if (this.mode !== 'shared' || this.stopped || gen <= this.workerGen) return;
    this.workerGen = gen;
    this.workerName = decoratedWorkerName(this.workerBaseName, this.workerGen);
    this.greetTimeoutRetriedCurrentGen = false;
    console.warn(`[Hydra] generation migration (${source}) → '${this.workerName}' (gen ${this.workerGen})`);
    this.teardownSharedPort();
    this.startShared();
  }

  /** Sickness detector: fires only when a fresh attach produced zero port
   *  messages for GREET_TIMEOUT_MS. Retry the current generation once (a
   *  transiently-hosed port deserves a second chance), then escape. The
   *  alternation also caps pathological churn: an environment where NOTHING
   *  ever attaches mints a generation at most every 2×GREET_TIMEOUT_MS,
   *  versus the pre-escape behavior of a doomed reattach every ~7 s. */
  private armGreetTimeout(): void {
    this.clearGreetTimeout();
    this.greetTimeoutTimer = setTimeout(() => {
      if (this.stopped || this.mode !== 'shared') return;
      if (this.greetTimeoutRetriedCurrentGen) {
        this.escapeShared(`no port message within ${this.GREET_TIMEOUT_MS}ms of a retried attach`);
      } else {
        this.greetTimeoutRetriedCurrentGen = true;
        console.warn(`[Hydra] mute attach: retrying '${this.workerName}' (gen ${this.workerGen}) once before escaping`);
        this.teardownSharedPort();
        this.startShared();
      }
    }, this.GREET_TIMEOUT_MS);
  }

  private clearGreetTimeout(): void {
    if (this.greetTimeoutTimer) {
      clearTimeout(this.greetTimeoutTimer);
      this.greetTimeoutTimer = null;
    }
  }

  /** Idempotent install of the peer-escape observer. `storage` fires only in
   *  OTHER tabs, so a healthy tab learns a peer escaped within milliseconds
   *  of the write and migrates — the convergence bound DNL-R13-P1 asked for. */
  private installStorageListener(): void {
    if (this.storageListener != null) return;
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    this.storageListener = (ev: StorageEvent) => {
      if (ev.key !== WORKER_GEN_STORAGE_PREFIX + this.workerBaseName) return;
      const gen = ev.newValue == null ? NaN : Number.parseInt(ev.newValue, 10);
      if (!Number.isFinite(gen)) return;
      this.migrateToGeneration(gen, 'peer tab escaped');
    };
    window.addEventListener('storage', this.storageListener);
  }

  private removeStorageListener(): void {
    if (this.storageListener != null && typeof window !== 'undefined') {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }
  }

  /** Local port teardown shared by every shared-mode recovery path. Does NOT
   *  post __bye — the worker's lastKnownNeeds cache should survive for the
   *  replacement port to pick up. */
  private teardownSharedPort(): void {
    this.stopWorkerHeartbeat();
    this.stopStallDetector();
    this.clearGreetTimeout();
    if (this.port) {
      try { this.port.close(); } catch { /* ignore */ }
      this.port = null;
    }
    this.worker = null;
    this.setConnectedState(false);
    this.setBackpressureLevel(0);
    this.callbacks.onStatus(false);
  }

  // ── Direct-WS path ─────────────────────────────────────────────

  private connect(): void {
    if (this.stopped) return;
    this.mode = 'direct';

    console.log(`[Hydra] connecting to ${this.url}...`);

    try {
      this.ws = new WebSocket(this.url);
      // Socket identity guard (Danielle R1 DNL-001): forceReconnect() closes
      // one socket and opens the next immediately, and the browser may
      // deliver the OLD socket's close (or a straggling message) AFTER the
      // replacement has opened and installed its fence. Every handler below
      // therefore speaks only while its socket is still `this.ws`; a
      // superseded socket's callbacks mutate nothing — no fence reset, no
      // status flip, no extra reconnect.
      const sock = this.ws;
      const owned = (): boolean => this.ws === sock;

      this.ws.onopen = () => {
        if (!owned()) return;
        this.setConnectedState(true);
        this.reconnectAttempts = 0;
        console.log('[Hydra] connected');
        this.callbacks.onStatus(true);
        // Socket boundary: nothing from this socket is admissible until ITS
        // ack installs a pair (A2′). The filter itself is carried across by
        // subscribeMarketsChannel below — client-side failover carry. A
        // degraded socket's fallback does not carry over: every socket
        // retries the filter.
        this.directFence.reset();
        this.directLegacyFallback = false;
        this.clearDirectAckTimer();

        // Dedicated OP transport: only the oddspapi channel — never
        // markets/unabated (the fleet's other connections own those).
        if (!this.opOnly) {
          // Markets subscription, scoped to the current sport filter (or broad
          // when none declared / '*'). Orderbooks channel is filtered to
          // declared needs — replay on reconnect from current declared set.
          // Always snapshot: hydra's `markets` channel doesn't auto-snapshot on
          // subscribe (only `orderbooks` does), and skipping the snapshot leaves
          // slow-trading tickers blank until their next live frame.
          if (this.filterMode.enabled && this.lastDeclaredFilterNeeds == null) {
            if (this.deferredMarketsSubscribe) clearTimeout(this.deferredMarketsSubscribe);
            this.deferredMarketsSubscribe = setTimeout(() => {
              this.deferredMarketsSubscribe = null;
              if (owned()) this.subscribeMarketsChannel();
            }, HydraClient.FIRST_NEEDS_WAIT_MS);
          } else {
            this.subscribeMarketsChannel();
          }

          // Unabated consensus channel — sibling to markets/orderbooks on the
          // same WS. Subscribe with no `tickers` filter to receive every game's
          // consensus, then emits on ≥1¢ shift. The explicit snapshot is
          // required: as of 2026-05-29 hydra does NOT auto-snapshot on a bare
          // `subscribe` for this channel (verified by WS probe — bare subscribe
          // returns zero events; only `op: 'snapshot'` triggers the per-game
          // Full Game burst). Same pattern as the markets channel below.
          this.send({ op: 'subscribe', channel: 'unabated' });
          this.send({ op: 'snapshot',  channel: 'unabated' });
        }

        // OddsPapi comparison odds — OP-capable feeds only (DNL-003,
        // failover rev: the env-preferred source plus the OG standby). The
        // channel is EXCLUDED from the legacy `all` firehose (contract rule
        // 11: `all` has no snapshot, so its deltas would be baseline-less),
        // so the explicit subscribe + snapshot pair is the ONLY way to
        // receive state. Bare subscribe is silent, like `unabated`.
        if (this.subscribeOpOdds) {
          this.send({ op: 'subscribe', channel: 'oddspapi' });
          this.send({ op: 'snapshot',  channel: 'oddspapi' });
        }
        // OpticOdds trial channel — same handshake, hydracore upstreams
        // only (the flag is never set toward OG; see subscribeOoOdds).
        if (this.subscribeOoOdds) {
          this.send({ op: 'subscribe', channel: 'opticodds' });
          this.send({ op: 'snapshot',  channel: 'opticodds' });
        }

        // Clear upstream state (connection just dropped) and let reconcile
        // rebuild the orderbook subscription from the current needs set.
        this.directUpstreamOrderbookTickers.clear();
        for (const t of this.directPendingUnsubscribeTimers.values()) clearTimeout(t);
        this.directPendingUnsubscribeTimers.clear();
        this.reconcileDirectOrderbookSubscriptions();

        this.startPingLoop();
      };

      this.ws.binaryType = 'arraybuffer';
      this.ws.onmessage = (event) => {
        if (!owned()) return;
        try {
          const raw = event.data instanceof ArrayBuffer
            ? new TextDecoder().decode(event.data)
            : event.data as string;
          const msg = JSON.parse(raw) as HydraServerMsg;
          this._lastMessageMs = Date.now();
          this.handleMessage(msg);
        } catch (err) {
          console.warn('[Hydra] parse error:', err);
        }
      };

      this.ws.onclose = (event) => {
        if (!owned()) return; // a superseded socket's delayed close
        this.setConnectedState(false);
        this.stopPingLoop();
        this.clearDirectAckTimer();
        if (this.deferredMarketsSubscribe) { clearTimeout(this.deferredMarketsSubscribe); this.deferredMarketsSubscribe = null; }
        this.directFence.reset();
        // An unexpected close while at level >= 2 is almost certainly a
        // backpressure shed — level 3 is an abrupt terminate and the 4002
        // close frame may never be delivered on a saturated socket.
        if (this._backpressureLevel >= 2) {
          console.warn(`[Hydra] close after backpressure level ${this._backpressureLevel} — treating as backpressure shed`);
        }
        this.setBackpressureLevel(0);
        console.log(`[Hydra] disconnected (code=${event.code}, reason=${event.reason || 'none'}, clean=${event.wasClean})`);
        this.callbacks.onStatus(false);
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        if (!owned()) return;
        console.warn(`[Hydra] ws error — connection to ${this.url} failed. Check: 1) Is Hydra running? 2) Does the URL support WebSocket upgrade? 3) Is port accessible?`);
      };
    } catch (err) {
      console.error('[Hydra] connect failed:', err);
      this.scheduleReconnect();
    }
  }

  private send(msg: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  /** Direct-WS path: reconcile orderbook subscription to match needs.
   *  Adds subscribe immediately; removes are debounced 30 s so rapid
   *  view changes that touch the same tickers don't churn. Mirror of
   *  the SharedWorker-side logic in hydraWorker.ts. */
  private reconcileDirectOrderbookSubscriptions(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const desired = this.directOrderbookNeeds;

    const toAdd: string[] = [];
    for (const ticker of desired) {
      if (!this.directUpstreamOrderbookTickers.has(ticker)) toAdd.push(ticker);
      const pending = this.directPendingUnsubscribeTimers.get(ticker);
      if (pending) {
        clearTimeout(pending);
        this.directPendingUnsubscribeTimers.delete(ticker);
      }
    }

    for (const ticker of this.directUpstreamOrderbookTickers) {
      if (desired.has(ticker)) continue;
      if (this.directPendingUnsubscribeTimers.has(ticker)) continue;
      const timer = setTimeout(() => {
        this.directPendingUnsubscribeTimers.delete(ticker);
        if (this.directOrderbookNeeds.has(ticker)) return;
        this.directUpstreamOrderbookTickers.delete(ticker);
        this.send({ op: 'unsubscribe', channel: 'orderbooks', tickers: [ticker] });
      }, this.UNSUBSCRIBE_GRACE_MS);
      this.directPendingUnsubscribeTimers.set(ticker, timer);
    }

    if (toAdd.length > 0) {
      for (const t of toAdd) this.directUpstreamOrderbookTickers.add(t);
      this.send({ op: 'subscribe', channel: 'orderbooks', tickers: toAdd });
    }
  }

  /** Direct-WS onopen. Flag off: the legacy subscribe + snapshot
   *  (connectMarketOps — sport-unfiltered, props behind the sticky opt-in;
   *  see sportFilter.ts). Flag on: the subscribe carries the complete current filter (the aggregator
   *  seeds the defaults before the first connect), and the ack's replacement
   *  snapshot replaces the explicit snapshot op. */
  private subscribeMarketsChannel(): void {
    let filter: MarketFilter | null = null;
    if (this.filterMode.enabled) {
      const agg = this.ensureDirectFilterAggregator();
      // Needs declared before this socket existed (or on a previous one)
      // are folded in SYNCHRONOUSLY so the first subscribe already carries
      // the complete filter — never a default connect followed by a
      // widening replace + second snapshot.
      this.directSubscribing = true;
      try {
        if (this.lastDeclaredFilterNeeds != null && !agg.hasSource('tab')) agg.setNeeds('tab', this.lastDeclaredFilterNeeds);
        agg.flushPending();
      } finally {
        this.directSubscribing = false;
      }
      filter = this.directFilter;
    }
    for (const op of connectMarketOps(filter, this.directPropsWanted)) this.send(op);
    if (filter) {
      this.clearDirectAckTimer();
      const sock = this.ws;
      this.directAckTimer = setTimeout(() => {
        this.directAckTimer = null;
        if (this.ws === sock) this.degradeDirectToLegacy(`no filterAck within ${HydraClient.ACK_TIMEOUT_MS}ms`);
      }, HydraClient.ACK_TIMEOUT_MS);
    }
  }

  // ── Message handling ───────────────────────────────────────────

  private handleMessage(msg: HydraServerMsg): void {
    // Any parsed server frame — including pong — proves the transport is
    // alive. Data-freshness bookkeeping stays with the per-type handlers.
    this.callbacks.onKeepalive?.();
    switch (msg.type) {
      case 'market':
        // Direct-WS fencing (A2′): on a filtered socket only frames stamped
        // with the installed pair are admissible; everything else — an older
        // generation, a previous socket, an unstamped legacy frame — is
        // dropped before it can touch the store. Shared mode arrives here
        // already fenced by the worker.
        if (this.mode === 'direct' && this.filterMode.enabled && !this.opOnly && !this.directLegacyFallback && !this.directFence.admits(msg)) {
          this._marketFramesDropped++;
          break;
        }
        this._marketsReceived++;
        this.callbacks.onMarket(msg.market);
        break;
      case 'filterAck':
        if (this.mode === 'direct') {
          this.clearDirectAckTimer(); // the engine speaks the filter
          const res = this.directFence.onAck(msg);
          if (res.kind === 'installed') {
            this.directLegacyFallback = false; // a (late) ack re-arms the fence
            // Reset BEFORE any frame of this generation can be admitted —
            // the ack precedes them on the wire, and this handler runs
            // synchronously in wire order.
            this.callbacks.onFilterReset?.({ ...res.pair, scope: res.effective });
          } else if (res.kind === 'rejected') {
            console.warn(`[Hydra] markets filter rejected (${res.error}) — prior filter/generation retained`);
            // A rejected FIRST filter activates nothing server-side: degrade
            // rather than sit dark (the object is ours; the log shows why).
            this.degradeDirectToLegacy(`filter rejected: ${res.error}`);
          }
        }
        this.callbacks.onFilterAck?.(msg);
        break;
      case 'orderbook':
        this._orderbooksReceived++;
        // Orderbook frames resuming IS the level-2 recovery signal — hydra
        // suspends them entirely at level >= 2 and sends no "recovered"
        // message. Every frame is a full book, so state self-heals per
        // ticker; for quiet tickers, re-issuing the filtered subscribe
        // triggers hydra's per-ticker auto-snapshot (direct mode only —
        // in shared mode hydraWorker owns the subscription and does this).
        if (this._backpressureLevel >= 2) {
          this.setBackpressureLevel(0);
          if (this.mode === 'direct' && this.directUpstreamOrderbookTickers.size > 0) {
            this.send({
              op: 'subscribe',
              channel: 'orderbooks',
              tickers: [...this.directUpstreamOrderbookTickers],
            });
          }
        }
        this.callbacks.onOrderbook(msg.ticker, msg.orderbook);
        break;
      case 'consensus':
        this.callbacks.onConsensus(msg.consensus, msg.serverTs);
        break;
      case 'opOdds':
      case 'opSnapshotBegin':
      case 'opSnapshotEnd':
        // DNL-003: only an OP-capable client (the preferred source or the
        // OG standby) may dispatch; an oddspapi frame on any other feed is
        // dropped with a counted diagnostic. The active-source gate in
        // useHydra then keeps exactly one ingestion authority at any
        // instant.
        if (!this.subscribeOpOdds) {
          this._opFramesDropped++;
          if (this._opFramesDropped === 1) {
            console.warn(`[Hydra:${this.workerName}] dropping oddspapi frames — this feed is not the OP ingestion authority (DNL-003)`);
          }
          break;
        }
        // Stamped for EVERY capable feed, active or standby, before the
        // active-source gate upstream — the same "record delivery, then
        // gate" order onMarket uses. That is what makes it usable as
        // proof-of-service for a standby we are considering promoting.
        this._lastOpFrameMs = Date.now();
        if (msg.type === 'opOdds') this.callbacks.onOpOdds?.(msg);
        else if (msg.type === 'opSnapshotBegin') this.callbacks.onOpSnapshotBegin?.(msg);
        else this.callbacks.onOpSnapshotEnd?.(msg);
        break;
      case 'activeSports':
        // Complete replacement set — replace, never merge. Duplicates
        // re-arrive on every subscribe op (worker greet replays too).
        this.callbacks.onActiveSports?.(Array.isArray(msg.sports) ? msg.sports : []);
        break;
      case 'pong':
        // Keepalive acknowledged
        break;
      case 'backpressure':
        console.warn(`[Hydra] backpressure level=${msg.level} pending=${msg.pending}: ${msg.message}`);
        // Track it so orderbook-driven views can show a stale indicator
        // while hydra has our orderbook frames suspended (level >= 2).
        // No proactive reconnect: market + consensus still flow at level
        // 2, and hydra's server-side level2EscalateAfterMs force-
        // disconnects hopeless consumers (abrupt terminate — the 4002
        // close frame may never arrive), which our existing
        // reconnect-with-backoff handles.
        this.setBackpressureLevel(typeof msg.level === 'number' ? msg.level : 0);
        break;
      case 'error':
        console.error(`[Hydra] error: ${msg.message}`);
        break;
    }
  }

  // ── SharedWorker heartbeat ─────────────────────────────────────

  /** Liveness ping to the SharedWorker so its reaper knows this tab is
   *  alive. Without this, a tab that dies without firing pagehide (crash,
   *  OS kill, mobile background evict) leaves its declared orderbook needs
   *  in the worker's union forever, monotonically growing the upstream
   *  subscription. See `hydraWorker.ts` reaper for the matching half. */
  private startWorkerHeartbeat(): void {
    this.stopWorkerHeartbeat();
    this.workerHeartbeatTimer = setInterval(() => {
      if (!this.port) return;
      try { this.port.postMessage({ type: '__heartbeat' }); } catch { /* port closing */ }
    }, this.WORKER_HEARTBEAT_INTERVAL_MS);
  }

  private stopWorkerHeartbeat(): void {
    if (this.workerHeartbeatTimer) {
      clearInterval(this.workerHeartbeatTimer);
      this.workerHeartbeatTimer = null;
    }
  }

  /** Tab-side stall detector. Fires every TAB_STALL_CHECK_INTERVAL_MS;
   *  if we still think we're connected but the port has gone silent for
   *  TAB_STALL_THRESHOLD_MS, the worker has stopped feeding us (port
   *  reaped during a freeze, worker died, etc) and we restart the
   *  SharedWorker connection. The worker's own watchdogs cover the
   *  upstream-silence side; this only catches gaps in the local
   *  port→tab leg that the worker can't observe. Counterpart to the
   *  worker's `reaper.dropped` / `port.resurrect` events. */
  private startStallDetector(): void {
    this.stopStallDetector();
    this.stallDetectorTimer = setInterval(() => {
      if (this.stopped) return;
      if (!this._connected) return;
      if (this._lastMessageMs === 0) return;
      const ageMs = Date.now() - this._lastMessageMs;
      if (ageMs > this.TAB_STALL_THRESHOLD_MS) {
        this.restartShared(`port silent for ${ageMs}ms`);
      }
    }, this.TAB_STALL_CHECK_INTERVAL_MS);
  }

  private stopStallDetector(): void {
    if (this.stallDetectorTimer) {
      clearInterval(this.stallDetectorTimer);
      this.stallDetectorTimer = null;
    }
  }

  // ── Ping / Reconnect (direct-WS path only) ────────────────────

  private startPingLoop(): void {
    this.stopPingLoop();
    this.pingTimer = setInterval(() => {
      this.send({ op: 'ping', ts: Date.now() });
    }, PING_INTERVAL);
  }

  private stopPingLoop(): void {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay = Math.min(BASE_RECONNECT_DELAY * 2 ** this.reconnectAttempts, MAX_RECONNECT_DELAY);
    this.reconnectAttempts++;
    console.log(`[Hydra] reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private clearTimers(): void {
    this.stopPingLoop();
    this.clearDirectAckTimer();
    if (this.deferredMarketsSubscribe) { clearTimeout(this.deferredMarketsSubscribe); this.deferredMarketsSubscribe = null; }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    for (const t of this.directPendingUnsubscribeTimers.values()) clearTimeout(t);
    this.directPendingUnsubscribeTimers.clear();
  }
}

/** Structured-clone-safe copy of a needs declaration for postMessage (Sets
 *  and iterables become arrays; `'all'` survives verbatim). */
function serializeNeeds(needs: FeedNeeds | null): { classes?: string[]; marketTypes?: string[]; sports?: string[] | 'all' } | null {
  if (needs == null) return null;
  return {
    classes: needs.classes ? [...needs.classes] : undefined,
    marketTypes: needs.marketTypes ? [...needs.marketTypes] : undefined,
    sports: needs.sports === undefined ? undefined : needs.sports === 'all' ? 'all' : [...needs.sports],
  };
}
